// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CartLineContainer, { CartLineContainerStyles } from "@ces/App/Components/Cart/CartLineContainer";
import LinesHeader, { LinesHeaderStyles } from "@ces/App/Components/Cart/LinesHeader";
import QtyField, { QtyFieldStyles } from "@ces/App/Components/Qty/QtyField";
import xUseWidgetBadges from "@ces/App/Hooks/xUseWidgetBadges";
import ProductImageBadges from "@ces/Overrides/Components/ProductImageBadges";
import ProductTextBadges from "@ces/Overrides/Components/ProductTextBadges";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasOnComplete } from "@insite/client-framework/HandlerCreator";
import { getIsShareEntity } from "@insite/client-framework/ServerSideRendering";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import loadProducts from "@insite/client-framework/Store/Data/Products/Handlers/LoadProducts";
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import addToWishList, {
    AddToWishListParameter,
} from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { OrderModel, ProductModel, WishListSettingsModel } from "@insite/client-framework/Types/ApiModels";
import CartLinesListHeader, { CartLinesListHeaderStyles } from "@insite/content-library/Components/CartLinesListHeader";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import VmiBinDetailTypeLink from "@insite/content-library/Components/VmiBinDetailTypeLink";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { HiddenProps } from "@insite/mobius/Hidden";
import { Clickable } from "@insite/mobius/index";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useMemo } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    order?: OrderModel;
    extendedStyles?: OrderLinesListStyles;
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    // XNG-Change
    const ids = (props.order?.orderLines as any).map((line: { productNumber: string }) => line.productNumber) || [];
    const productParams = {
        productNumbers: [...new Set(ids)],
        expand: ["detail", "badges", "warehouses", "images", "properties"],
    };
    return {
        wishListSettings: getSettingsCollection(state).wishListSettings,
        enableVat: getSettingsCollection(state).productSettings.enableVat,
        vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
        vmiBinDetailsPageUrl: getPageLinkByPageType(state, "VmiBinDetailsPage")?.url,
        showPricing: state.context.showPricing,
        productsLines: getProductsDataView(state, productParams as any),
        productParams,
        // XNG-Changes
        isOrderDetailsPage: getCurrentPage(state).type === "OrderDetailsPage",
    };
};

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
    // XNG-Change
    loadProducts,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OrderLineNotesStyles {
    typographyStyles?: TypographyProps;
    gridItemStyles?: GridItemProps;
}

export interface OrderLinesListStyles {
    gridContainer?: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyProps;
    orderLineCardContainer?: GridContainerProps;
    orderLineCardGridItem?: GridItemProps;
    orderLineCardGridContainer?: GridContainerProps;
    orderLineCardImageGridItem?: GridItemProps;
    productImageLink?: LinkPresentationProps;
    orderLineCardImage?: LazyImageProps;
    orderLineCardInfoGridItem?: GridItemProps;
    orderLineCardInfoGridContainer?: GridContainerProps;
    orderLineCardAddToListGridItem?: GridItemProps;
    orderLineCardActionsWide?: HiddenProps;
    orderLineCardActionsNarrow?: HiddenProps;
    overflowMenu?: OverflowMenuPresentationProps;
    addToListClickable?: ClickablePresentationProps;
    orderLineCardAddToListButton?: ButtonPresentationProps;
    productBrandStyles?: ProductBrandStyles;
    productInfoLink?: LinkPresentationProps;
    productInfoDescription?: TypographyProps;
    productInfoSectionOptionsGridItem?: GridItemProps;
    productInfoSectionOptionsList?: InjectableCss;
    productInfoSectionOptionsListItem?: InjectableCss;
    productInfoPartNumbersGridItem?: GridItemProps;
    productInfoPartNumbersGridContainer?: GridContainerProps;
    productInfoSectionOptionsText?: InjectableCss;
    productInfoGridItem?: GridItemProps;
    productInfoGridContainer?: GridContainerProps;
    productInfoBrandDescriptionGridItem?: GridItemProps;
    productInfoBrandDescriptionGridContainer?: GridContainerProps;
    productInfoBrandGridItem?: GridItemProps;
    productInfoDescriptionGridItem?: GridItemProps;
    productDescriptionStyles?: ProductDescriptionStyles;
    productInfoErpNumberGridItem?: GridItemProps;
    erpNumberHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoManufacturerItemGridItem?: GridItemProps;
    productInfoVmiBinNumberGridItem?: GridItemProps;
    manufacturerHeadingAndText?: SmallHeadingAndTextStyles;
    vmiBinNumberHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoCustomerProductGridItem?: GridItemProps;
    customerProductNumberHeadingAndText?: SmallHeadingAndTextStyles;
    orderLineInfoPromotionGridItem?: GridItemProps;
    orderLineInfoPromotionList?: InjectableCss;
    orderLineInfoPromotionText?: TypographyProps;
    orderLineInfoGridItem?: GridItemProps;
    orderLineInfoGridContainer?: GridContainerProps;
    orderLinePriceGridItem?: GridItemProps;
    priceHeadingAndText?: SmallHeadingAndTextStyles;
    vatLabelText?: TypographyPresentationProps;
    secondaryPriceText?: TypographyPresentationProps;
    secondaryVatLabelText?: TypographyPresentationProps;
    productInfoQtyOrderedGridItem?: GridItemProps;
    productInfoQtyOrderedHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoQtyShippedGridItem?: GridItemProps;
    productInfoQtyShippedHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoSubtotalGridItem?: GridItemProps;
    subtotalWithoutVatHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoSubtotalHeadingAndText?: SmallHeadingAndTextStyles;
    orderLineNotes?: OrderLineNotesStyles;
    productInfoTotalsGridItem?: GridItemProps;
    productInfoTotalsGridContainer?: GridContainerProps;
    // XNG-Changes
    shippedProductInfoSubtotalHeadingAndText?: SmallHeadingAndTextStyles;
    unShippedProductInfoSubtotalHeadingAndText?: SmallHeadingAndTextStyles;
    productOrderPriceContainer?: InjectableCss;
    productInfoSectionOptionsLableText?: TypographyProps;
    productInfoSectionOptionsValueText?: TypographyProps;
    productPartNumbers?: ProductPartNumbersStyles;
    promotionsContainer?: InjectableCss;
    readOnlyNotesText?: SmallHeadingAndTextStyles;
    addToListLink?: LinkPresentationProps;
    promotionNameLabel?: TypographyPresentationProps;
    quantityTextField?: TextFieldPresentationProps;
    lableText?: TypographyPresentationProps;
    qtyOrderedField?: QtyFieldStyles;
    qtyUnShippedField?: QtyFieldStyles;
    productQtyContainer?: InjectableCss;
    priceHeading?: TypographyProps;
    priceText?: TypographyProps;
    unitOfMeasure?: TypographyProps;
    priceWrapper?: InjectableCss;
    cartLineContainerStyles?: CartLineContainerStyles;
    linesHeaderStyles?: LinesHeaderStyles;
    cartLinesListHeaderStyles?: CartLinesListHeaderStyles;
    badgesContainer?: InjectableCss;
}

export const orderLinesListStyles: OrderLinesListStyles = {
    gridContainer: {
        gap: 10,
    },
    titleGridItem: {
        width: 12,
    },
    titleText: {
        variant: "h5",
        as: "h2",
        css: css`
            @media print {
                font-size: 15px;
                padding-top: 15px;
                margin-bottom: 5px;
            }
            padding-top: 30px;
            margin-bottom: 0.5rem;
        `,
    },
    orderLineCardContainer: {
        gap: 10,
    },
    orderLineCardGridItem: {
        width: 12,
    },
    orderLineCardImageGridItem: {
        width: 2,
        css: css`
            @media print {
                max-height: 83px;
                max-width: 83px;
                padding: 15px 0 0 0;
            }
        `,
    },
    orderLineCardInfoGridItem: {
        width: [8, 8, 7, 8, 8],
        printWidth: 10,
        css: css`
            @media print {
                &&&& {
                    max-width: 100%;
                }
            }
        `,
    },
    orderLineCardInfoGridContainer: {
        gap: 10,
    },
    orderLineCardAddToListGridItem: {
        width: [2, 2, 3, 2, 2],
        printWidth: 0,
    },
    orderLineCardActionsWide: {
        above: "sm",
    },
    orderLineCardActionsNarrow: {
        below: "md",
    },
    productInfoSectionOptionsGridItem: {
        width: 12,
    },
    productInfoPartNumbersGridItem: {
        width: 12,
    },
    productInfoGridItem: {
        width: [12, 12, 12, 5, 5],
        printWidth: 6,
    },
    productInfoGridContainer: {
        gap: 5,
    },
    productInfoBrandDescriptionGridItem: {
        width: 12,
    },
    productInfoBrandDescriptionGridContainer: {
        gap: 5,
    },
    productInfoBrandGridItem: {
        width: 12,
    },
    productInfoDescriptionGridItem: {
        width: 12,
    },
    productInfoErpNumberGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    productInfoManufacturerItemGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    productInfoVmiBinNumberGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    productInfoCustomerProductGridItem: {
        width: [12, 12, 12, 12, 12],
        printWidth: 12,
    },
    orderLineInfoPromotionGridItem: {
        width: 12,
    },
    orderLineInfoPromotionText: {
        size: 12,
        italic: true,
    },
    orderLineInfoGridItem: {
        width: [12, 12, 12, 7, 7],
        printWidth: 6,
    },
    orderLineInfoGridContainer: {
        gap: 5,
    },
    orderLinePriceGridItem: {
        width: [12, 12, 12, 12, 12],

        css: css`
            flex-direction: column;
        `,
    },
    vatLabelText: {
        size: 12,
    },
    secondaryVatLabelText: {
        size: 12,
    },
    productInfoQtyOrderedGridItem: {
        width: [6, 6, 4, 3, 3],
        printWidth: 4,
    },
    productInfoQtyShippedGridItem: {
        width: [6, 6, 4, 3, 3],
        printWidth: 4,
    },
    productInfoSubtotalGridItem: {
        width: [12, 12, 4, 6, 6],
        printWidth: 4,
    },
    orderLineNotes: {
        gridItemStyles: {
            width: 12,
        },
    },
    productInfoTotalsGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            padding-top: 10px;
        `,
    },
    productInfoTotalsGridContainer: {
        gap: 10,
    },
    productInfoQtyOrderedHeadingAndText: {
        extendedStyles: {
            wrapper: {
                css: css`
                    padding-right: 10px;
                `,
            },
            heading: {
                variant: "legend",
                weight: "bold",
            },

            text: {
                css: css`
                    text-align: center;
                `,
            },
        },
    },
    productInfoQtyShippedHeadingAndText: {
        extendedStyles: {
            wrapper: {
                css: css`
                    padding-right: 10px;
                `,
            },
            heading: {
                variant: "legend",
                weight: "bold",
            },

            text: {
                css: css`
                    text-align: center;
                `,
            },
        },
    },
    productInfoSubtotalHeadingAndText: {
        wrapper: {
            css: css`
                padding-right: 10px;
            `,
        },
        heading: {
            variant: "legend",
            weight: "bold",
        },

        text: {
            weight: "bold",
            css: css`
                text-align: center;
            `,
        },
    },
    priceHeadingAndText: {
        extendedStyles: {
            heading: {
                weight: "bold",
            },
            text: {
                weight: "bold",
            },
        },
    },
    customerProductNumberHeadingAndText: {
        extendedStyles: {
            heading: {
                weight: "bold",
            },
        },
    },
    erpNumberHeadingAndText: {
        extendedStyles: {
            heading: {
                weight: "bold",
            },
        },
    },
    manufacturerHeadingAndText: {
        extendedStyles: {
            heading: {
                weight: "bold",
            },
        },
    },
    vmiBinNumberHeadingAndText: {
        extendedStyles: {
            heading: {
                weight: "bold",
            },
        },
    },
};

// XNG-Changes: promotion to be in Order Line Product Info component
const OrderLineProductInfo = ({
    orderLine,
    order,
    styles,
    vmiLocationId,
    realTimeOrderLine,
}: {
    orderLine: any;
    order: OrderModel;
    styles: OrderLinesListStyles;
    vmiLocationId: string;
    realTimeOrderLine: any;
}) => {
    let sectionOptions: JSX.Element[] = [];
    if (orderLine.sectionOptions && orderLine.sectionOptions) {
        sectionOptions = orderLine.sectionOptions.map(
            (option: {
                sectionOptionId: { toString: () => React.Key | undefined };
                sectionName: boolean | React.ReactChild | React.ReactFragment | React.ReactPortal | null | undefined;
                optionName: boolean | React.ReactChild | React.ReactFragment | React.ReactPortal | null | undefined;
            }) => (
                <StyledWrapper {...styles.productInfoSectionOptionsListItem} key={option.sectionOptionId.toString()}>
                    <Typography {...styles.productInfoSectionOptionsLableText}>{option.sectionName}</Typography>
                    <Typography {...styles.productInfoSectionOptionsValueText}>{option.optionName}</Typography>
                </StyledWrapper>
            ),
        );
    }

    let promotions: JSX.Element[] = [];
    if (order.orderPromotions) {
        promotions = order.orderPromotions
            .filter(promotion => promotion.orderHistoryLineId === orderLine.id)
            .map((promotion, index) => (
                <StyledWrapper key={promotion.id}>
                    <Typography {...styles.promotionNameLabel}>{`${translate("Promotion")} #${index + 1}`}</Typography>
                    <Typography {...styles.orderLineInfoPromotionText}>{promotion.name}</Typography>
                </StyledWrapper>
            ));
    }

    const { imageBadges, textBadges } = xUseWidgetBadges({
        productContext: {
            product: orderLine,
        } as any,
        showTextBadges: true,
        showImageBadges: true,
    });
    return (
        <>
            {orderLine.brand && (
                <ProductBrand brand={orderLine.brand} showLogo={false} extendedStyles={styles.productBrandStyles} />
            )}
            {vmiLocationId && orderLine.isActiveVmiBin && (
                <VmiBinDetailTypeLink
                    title={orderLine.shortDescription}
                    locationId={vmiLocationId}
                    id={orderLine.vmiBinId}
                />
            )}
            {vmiLocationId && !orderLine.isActiveVmiBin && <Typography as="p">{orderLine.shortDescription}</Typography>}
            {/* XNG-Change */}
            {!vmiLocationId && (
                <ProductDescription
                    product={{ ...orderLine, shortDescription: realTimeOrderLine.description }}
                    extendedStyles={styles.productDescriptionStyles}
                />
            )}
            {/* XNG-Change */}
            <ProductPartNumbers
                productNumber={orderLine.productNumber || realTimeOrderLine.productNumber}
                customerProductNumber={orderLine.customerName}
                manufacturerItem={orderLine.manufacturerItem}
                extendedStyles={styles.productPartNumbers}
            />
            {(!!textBadges?.length || !!imageBadges?.length) && (
                <StyledWrapper {...styles.badgesContainer}>
                    <ProductTextBadges badgeList={textBadges as any} maxBadges={1} />
                    <ProductImageBadges badgeList={imageBadges as any} maxBadges={4} />
                </StyledWrapper>
            )}
            {sectionOptions.length > 0 && (
                <StyledWrapper {...styles.productInfoSectionOptionsGridItem}>{sectionOptions}</StyledWrapper>
            )}
            {promotions && promotions.length > 0 && (
                <StyledWrapper {...styles.promotionsContainer}>{promotions}</StyledWrapper>
            )}
            {orderLine.vmiBinNumber && (
                <StyledWrapper {...styles.productInfoVmiBinNumberGridItem}>
                    <SmallHeadingAndText
                        {...styles.vmiBinNumberHeadingAndText}
                        heading={translate("Bin #")}
                        text={orderLine.vmiBinNumber}
                    />
                </StyledWrapper>
            )}
        </>
    );
};

const mapStateToOrderLineInfoProps = (state: ApplicationState) => ({
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
});

// XNG-Change: splitting qty, price and total to follow the new structure pattern
const OrderLineInfoProto = ({
    orderLine,
    order,
    enableVat,
    vatPriceDisplay,
    styles,
    showPricing,
    realTimeOrderLine,
    // XNG-Changes
    isOrderDetailsPage,
}: {
    orderLine: any;
    realTimeOrderLine: any;
    order?: OrderModel;
    enableVat: boolean;
    vatPriceDisplay: string;
    styles: OrderLinesListStyles;
    showPricing?: boolean;
    isOrderDetailsPage?: boolean;
}) => {
    // XNG-Changes: Move promotions to OrderLineProductInfo
    return {
        Qty: (
            <StyledWrapper {...styles.productQtyContainer}>
                <StyledWrapper>
                    {/* XNG-Changes */}
                    {!isOrderDetailsPage && (
                        <QtyField
                            label={translate("QTY Ordered")}
                            editable={false}
                            value={realTimeOrderLine.qtyOrdered}
                            extendedStyles={styles.qtyOrderedField}
                        />
                    )}
                    <QtyField
                        label={translate("QTY Shipped")}
                        editable={false}
                        value={realTimeOrderLine.qtyShipped}
                        extendedStyles={styles.qtyOrderedField}
                    />
                    {/* XNG-Changes */}
                    {isOrderDetailsPage && (
                        <QtyField
                            label={translate("QTY Not Shipped")}
                            editable={false}
                            value={realTimeOrderLine.qtyUnShipped}
                            extendedStyles={styles.qtyUnShippedField}
                        />
                    )}
                </StyledWrapper>
            </StyledWrapper>
        ),
        Price: (
            <StyledWrapper {...styles.productOrderPriceContainer} data-test-selector="productOrderPriceContainer">
                {showPricing && (
                    <>
                        <StyledWrapper {...styles.priceWrapper}>
                            <Typography {...styles.priceHeading}>{translate("Price/Unit")}</Typography>
                            <Typography {...styles.priceText}>
                                {enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                    ? realTimeOrderLine?.unitPriceWithVatDisplay
                                    : realTimeOrderLine?.unitPriceDisplay}
                            </Typography>
                            <Typography {...styles.unitOfMeasure}>
                                {realTimeOrderLine?.pricingUnitOfMeasure
                                    ? ` / ${realTimeOrderLine.pricingUnitOfMeasure}`
                                    : realTimeOrderLine?.unitOfMeasure
                                    ? ` / ${realTimeOrderLine.unitOfMeasure}`
                                    : ""}
                            </Typography>
                        </StyledWrapper>
                        {enableVat && (
                            <>
                                <Typography as="p" {...styles.vatLabelText}>
                                    {vatPriceDisplay === "DisplayWithVat" ||
                                    vatPriceDisplay === "DisplayWithAndWithoutVat"
                                        ? `${translate("Inc. VAT")} (${realTimeOrderLine.taxRate}%)`
                                        : translate("Ex. VAT")}
                                </Typography>
                                {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                    <>
                                        <Typography {...styles.secondaryPriceText} as="p">
                                            {realTimeOrderLine.unitPriceDisplay +
                                                (realTimeOrderLine.unitOfMeasure
                                                    ? ` / ${realTimeOrderLine.unitOfMeasure}`
                                                    : "")}
                                        </Typography>
                                        <Typography as="p" {...styles.secondaryVatLabelText}>
                                            {translate("Ex. VAT")}
                                        </Typography>
                                    </>
                                )}
                            </>
                        )}
                    </>
                )}
            </StyledWrapper>
        ),
        Total: (
            <StyledWrapper {...styles.productOrderPriceContainer}>
                {showPricing && (
                    <>
                        {enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                            <SmallHeadingAndText
                                {...styles.subtotalWithoutVatHeadingAndText}
                                heading={`${translate("Total")} (${translate("Ex. VAT")})`}
                                text={realTimeOrderLine.extendedUnitNetPriceDisplay}
                            />
                        )}
                        <SmallHeadingAndText
                            {...(isOrderDetailsPage
                                ? styles.shippedProductInfoSubtotalHeadingAndText
                                : styles.productInfoSubtotalHeadingAndText)}
                            heading={
                                !enableVat
                                    ? isOrderDetailsPage
                                        ? translate("Shipped Total")
                                        : translate("Total")
                                    : vatPriceDisplay !== "DisplayWithoutVat"
                                    ? `${translate("Total")} (${translate("Inc. VAT")})`
                                    : `${translate("Total")} (${translate("Ex. VAT")})`
                            }
                            text={
                                enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                    ? realTimeOrderLine.netPriceWithVatDisplay
                                    : isOrderDetailsPage
                                    ? realTimeOrderLine.shippedTotalDisplay
                                    : realTimeOrderLine.extendedUnitNetPriceDisplay
                            }
                        />
                        {/* XNG-Changes */}
                        {isOrderDetailsPage && (
                            <SmallHeadingAndText
                                {...styles.unShippedProductInfoSubtotalHeadingAndText}
                                heading={translate("Not Shipped Total")}
                                text={realTimeOrderLine.unShippedTotalDisplay}
                            />
                        )}
                    </>
                )}
            </StyledWrapper>
        ),
    };
};

// const OrderLineInfo = connect(mapStateToOrderLineInfoProps)(OrderLineInfoProto);

// XNG-Changes: HTML structure
// const OrderLineNotes = ({ orderLine, extendedStyles }: { orderLine: OrderLineModel; extendedStyles?: any }) => {
//     return orderLine.notes ? (
//         <SmallHeadingAndText extendedStyles={extendedStyles} heading={translate("Line Notes")} text={orderLine.notes} />
//     ) : null;
// };

const OrderLineCard = (props: {
    orderLine: any;
    order: OrderModel;
    wishListSettings?: WishListSettingsModel;
    vmiBinDetailsPageUrl?: string;
    styles: OrderLinesListStyles;
    setAddToListModalIsOpen: (parameter: {
        productInfos?: Omit<ProductInfo, "productDetailPath">[];
        modalIsOpen: boolean;
    }) => void;
    addToWishList: (parameter: AddToWishListParameter & HasOnComplete) => void;
    // XNG-Changes
    className?: string;
    enableVat: boolean;
    vatPriceDisplay: string;
    showPricing?: boolean;
    isOrderDetailsPage?: boolean;
    realTimeOrderLine: any;
}) => {
    const {
        orderLine,
        order,
        wishListSettings,
        vmiBinDetailsPageUrl,
        styles,
        setAddToListModalIsOpen,
        addToWishList,
        enableVat,
        vatPriceDisplay,
        showPricing,
        realTimeOrderLine,
        // XNG-Changes
        isOrderDetailsPage,
    } = props;
    const toasterContext = React.useContext(ToasterContext);
    const addToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        // XNG-Change: using realTimeOrderLine if doesn't exist in orderLine
        // Using orderline.id bc orderline passed as a Product Model
        const productInfo = {
            productId: orderLine.productId || orderLine.id,
            qtyOrdered: realTimeOrderLine.qtyOrdered || orderLine.qtyOrdered,
            unitOfMeasure: realTimeOrderLine.unitOfMeasure || orderLine.unitOfMeasure,
        };

        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    // XNG-Changes
    const orderLineInfo = OrderLineInfoProto({
        orderLine,
        order,
        styles,
        enableVat,
        vatPriceDisplay,
        showPricing,
        realTimeOrderLine,
        // XNG-Changes
        isOrderDetailsPage,
    });

    const isShareEntity = getIsShareEntity();

    // XNG-Changes: using cartLine container global structure
    return (
        <CartLineContainer
            data-test-selector={`orderLineCard${realTimeOrderLine?.id}`}
            className={props.className}
            extendedStyles={styles.cartLineContainerStyles}
        >
            <CartLineContainer.Image>
                {orderLine.productUri && !order.vmiLocationId && (
                    <Clickable
                        {...styles.productImageLink}
                        href={orderLine.productUri}
                        aria-label="share entity clickable"
                    >
                        <LazyImage
                            {...styles.orderLineCardImage}
                            src={orderLine.mediumImagePath}
                            isShareEntity={isShareEntity}
                            // XNG-Changes: use imageAltText from the products API
                            altText={orderLine.imageAltText || realTimeOrderLine.description}
                        />
                    </Clickable>
                )}
                {orderLine.productUri && order.vmiLocationId && orderLine.isActiveVmiBin && (
                    <Clickable
                        {...styles.productImageLink}
                        href={`${vmiBinDetailsPageUrl}?locationId=${order.vmiLocationId}&id=${orderLine.vmiBinId}`}
                        aria-label="share entity clickable"
                    >
                        <LazyImage
                            {...styles.orderLineCardImage}
                            src={orderLine.mediumImagePath}
                            isShareEntity={isShareEntity}
                            // XNG-Changes: use imageAltText from the products API
                            altText={orderLine.imageAltText || realTimeOrderLine.description}
                        />
                    </Clickable>
                )}
                {!orderLine.productUri && (
                    <LazyImage
                        {...styles.orderLineCardImage}
                        src={orderLine.mediumImagePath}
                        isShareEntity={isShareEntity}
                        // XNG-Changes: use imageAltText from the products API
                        altText={orderLine.imageAltText || realTimeOrderLine.description}
                    />
                )}
            </CartLineContainer.Image>
            <CartLineContainer.Description>
                <OrderLineProductInfo
                    orderLine={orderLine}
                    styles={styles}
                    vmiLocationId={order.vmiLocationId}
                    order={order}
                    realTimeOrderLine={realTimeOrderLine}
                />
            </CartLineContainer.Description>
            <CartLineContainer.Qty>{orderLineInfo?.Qty}</CartLineContainer.Qty>
            <CartLineContainer.Price>{orderLineInfo?.Price}</CartLineContainer.Price>
            <CartLineContainer.Total>{orderLineInfo?.Total}</CartLineContainer.Total>
            <CartLineContainer.Notes>
                {/* <CartLineNotes cart={{ canModifyOrder: false } as CartModel} cartNotes={orderLine.notes as any} /> */}
                {orderLine.canAddToWishlist && (
                    <Link onClick={addToListClickHandler} {...styles.addToListLink} aria-label="Add to list">
                        {translate("Add to List")}
                    </Link>
                )}
            </CartLineContainer.Notes>
        </CartLineContainer>
    );
};

const OrderLinesList: FC<Props> = ({
    order,
    wishListSettings,
    setAddToListModalIsOpen,
    addToWishList,
    extendedStyles,
    enableVat,
    vatPriceDisplay,
    showPricing,
    loadProducts,
    productsLines,
    productParams,
    // XNG-Changes
    isOrderDetailsPage,
}) => {
    const [styles] = React.useState(() => mergeToNew(orderLinesListStyles, extendedStyles));

    // XNG-Change
    useEffect(() => {
        if (order?.orderLines && order?.orderLines.length > 0 && !productsLines.value && !productsLines.isLoading) {
            loadProducts(productParams as any);
        }
    }, [productParams]);

    const getProductByPNumber = useMemo(() => {
        if (!productsLines) {
            return;
        }

        return productsLines.value?.reduce((acc: { [key: string]: ProductModel }, productLine) => {
            acc[productLine.productNumber] = productLine;
            return acc;
        }, {});
    }, [productsLines]);

    // XNG-Change
    // if (!order || !order.orderLines) {
    //     return null;
    // }

    // XNG-Changes
    return (
        <StyledWrapper {...styles.gridContainer}>
            {!!order?.orderLines?.length && (
                <CartLinesListHeader
                    isCondensed={false}
                    hideCondensedSelector
                    productsCount={order.orderLines.length}
                    extendedStyles={styles.cartLinesListHeaderStyles}
                />
            )}
            <LinesHeader showMdScreenBorder={false} extendedStyles={styles.linesHeaderStyles} />
            {/* XNG-Change */}
            {order?.orderLines?.map(orderLine => (
                <OrderLineCard
                    key={(orderLine as any).productNumber}
                    realTimeOrderLine={orderLine}
                    orderLine={{
                        ...getProductByPNumber?.[(orderLine as any).productNumber],
                        productUri: getProductByPNumber?.[(orderLine as any).productNumber]?.canonicalUrl,
                        shortDescription: getProductByPNumber?.[(orderLine as any).productNumber]?.productTitle,
                    }}
                    order={order}
                    wishListSettings={wishListSettings}
                    styles={styles}
                    setAddToListModalIsOpen={setAddToListModalIsOpen}
                    addToWishList={addToWishList}
                    enableVat={enableVat}
                    vatPriceDisplay={vatPriceDisplay}
                    showPricing
                    isOrderDetailsPage
                />
            ))}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(OrderLinesList);
